import { Chart } from "chart.js";
import React, { useMemo, useRef, useState } from "react";
import { getElementAtEvent, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { useGetActivityReportQuery } from "src/store/api/events";
import { selectCurrentWorkspaceId } from "src/store/app";

import { selectGroups } from "@dashboard/groups/store/selectors";
import { DropdownItem } from "@devices/components/shared";
import DropdownContainer from "@devices/components/shared/Dropdown/DropdownContainer";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import TimeService from "@services/timeService/timeService";
import UserSettingsService from "@services/userSettingsService";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { LoadingIcon } from "@shared/Loading";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";
import { Tooltip } from "@shared/Tooltip";

import { EventOverviewModal } from "../EventOverviewModal/EventOverviewModal";

import { useExportEventsToCsv } from "./EventChartCard.export";
import { useStyles } from "./EventChartCard.styles";
import { ClickedPoint, getChartOptions, getEventColors, LogZeroBuffer, TimeRangeOptions } from "./EventChartCard.utils";

type Props = {
    groupIds: number[];
};

// When user selects N days, we want data from (N-1) days ago to today
// This ensures the chart shows exactly N days including today
const daysOffset = 1;

const chartHeight = 240;

const getDaysArray = (dayRange: number) => {
    const today = new Date();
    const totalDays = dayRange;

    return Array.from({ length: totalDays }, (_, i: number) => {
        const date = new Date(today);
        date.setDate(today.getDate() - (dayRange - daysOffset) + i);
        date.setHours(0, 0, 0, 0);
        return date.getTime();
    });
};

const getFromDate = (dayRange: number) => {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - (dayRange - daysOffset));
    return TimeService.getLocalDate(fromDate.getTime(), "MILLIS", "date");
};

export const EventChartCard = (props: Props) => {
    const [dayRange, setDayRange] = useState<number>(UserSettingsService.getOverviewPageDayRange());
    const [clickedPoint, setClickedPoint] = useState<ClickedPoint | null>(null);
    const [isActivityFeedOpen, setIsActivityFeedOpen] = useState<boolean>(false);

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);
    const theme = useTheme();
    const eventColors = useMemo(() => getEventColors(theme), [theme]);
    const chartOptions = getChartOptions(theme);

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const fromDate = getFromDate(dayRange);
    const groups = useSelector(selectGroups);
    const reportGroupIds = props.groupIds.length > 0 ? props.groupIds : groups.map((g) => g.id);

    const activityReport = useGetActivityReportQuery(
        workspaceId
            ? {
                  workspaceId,
                  fromDate,
                  groupIds: reportGroupIds,
                  allWorkspaceGroupsLength: groups.length,
              }
            : skipToken,
    );
    const { data, isLoading, isFetching } = activityReport;
    useQueryErrorToast(activityReport);

    const { onExport, isExporting } = useExportEventsToCsv(reportGroupIds, dayRange, fromDate);

    const chartRef = useRef<Chart<"line", { x: number; y: number }[], unknown> | null>(null);
    const menuAnchorRef = useRef<HTMLButtonElement | null>(null);

    const { chartData, counts } = useMemo(() => {
        const eventLevels: API.EventLevel[] = ["notice", "warning"];
        const countMap: Record<API.EventLevel, Record<number, number>> = {
            notice: {},
            warning: {},
        };
        data?.list?.forEach((item) => {
            countMap[item.level][new Date(item.date).setHours(0, 0, 0, 0)] = item.count;
        });

        const days = getDaysArray(dayRange);
        const datasets = eventLevels.map((level) => ({
            label: t(`pages:overviewPage.cards.eventChart.${level}`),
            data: days.map((timestamp) => ({
                x: timestamp,
                y: countMap[level][timestamp] || LogZeroBuffer,
            })),
            backgroundColor: eventColors[level],
            borderColor: eventColors[level],
            tension: 0.3,
            fill: false,
            spanGaps: true,
            clip: false as const,
        }));

        return {
            allDays: days,
            counts: countMap,
            chartData: {
                labels: days,
                datasets: datasets,
            },
        };
    }, [dayRange, data?.list, t, eventColors]);

    const handlePointClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
        const chart = chartRef.current;
        const clickedElement = chart ? getElementAtEvent(chart, event)[0] : undefined;
        if (!clickedElement) {
            return;
        }

        const clickedDataset = chartData.datasets[clickedElement.datasetIndex];
        const selectedPoint = clickedDataset.data[clickedElement.index];

        const actualValue = selectedPoint.y === LogZeroBuffer ? 0 : selectedPoint.y;
        if (actualValue === 0) {
            return;
        }

        const clickedDatasetLabel = clickedDataset.label;
        let level: "notice" | "warning";

        if (clickedDatasetLabel === t("pages:overviewPage.cards.eventChart.notice")) {
            level = "notice";
        } else if (clickedDatasetLabel === t("pages:overviewPage.cards.eventChart.warning")) {
            level = "warning";
        } else {
            return;
        }

        const timestamp = selectedPoint.x;
        const noticeCount = counts.notice[timestamp] || 0;
        const warningCount = counts.warning[timestamp] || 0;

        const hasOverlappingEvents = noticeCount > 0 && warningCount > 0 && noticeCount === warningCount;

        setClickedPoint({
            epochMs: selectedPoint.x,
            count: actualValue,
            level,
            hasOverlappingEvents,
        });

        setIsActivityFeedOpen(true);
    };
    const handleDayRangeChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "number") {
            return;
        }
        const range = selectedOption.value;
        UserSettingsService.setWorkspaceValue("overviewPage.dayRange", range);
        setDayRange(range);
    };

    return (
        <React.Fragment>
            <SectionPanel
                className={classes.card}
                title={t("pages:overviewPage.cards.eventChart.title")}
                actionsPlacement="header"
                actions={
                    <Box className={classes.managementContainer}>
                        <SelectField
                            label={t("pages:overviewPage.cards.eventChart.dateRange")}
                            value={TimeRangeOptions.find((option) => option.value === dayRange)}
                            onChange={handleDayRangeChange}
                            options={TimeRangeOptions}
                            disabled={isLoading || isFetching}
                            disableSorting
                            fullWidth
                        />
                        <DropdownContainer
                            icon={<i className="fa-solid fa-ellipsis-vertical" />}
                            anchorRef={menuAnchorRef}
                            tooltipProps={{ title: t("common:tooltip.moreActions") }}
                        >
                            <DropdownItem
                                label={t("pages:overviewPage.cards.eventChart.exportActivity")}
                                iconName="fa-solid fa-fw fa-file-export"
                                onClick={onExport}
                                disabled={isExporting || !data?.list?.length}
                            />
                        </DropdownContainer>
                    </Box>
                }
            >
                {isLoading ? (
                    <Box className={classes.chartContainer}>
                        <Box className={classes.chartLoading} height={chartHeight}>
                            <Tooltip title={t("pages:overviewPage.tooltips.loadingMoreData")}>
                                <LoadingIcon size="block" />
                            </Tooltip>
                        </Box>
                    </Box>
                ) : null}
                {!isLoading && data?.list?.length ? (
                    <Box className={classes.chartContainer}>
                        <Line
                            ref={chartRef}
                            options={chartOptions}
                            data={chartData}
                            height={chartHeight}
                            onClick={(event) => {
                                handlePointClick(event);
                            }}
                        />
                    </Box>
                ) : null}
                {!isLoading && !data?.list?.length ? (
                    <Typography align="center" className={classes.noActivityText}>
                        {t("pages:overviewPage.cards.eventChart.noActivity")}
                    </Typography>
                ) : null}
            </SectionPanel>

            {isActivityFeedOpen && clickedPoint ? (
                <EventOverviewModal
                    groupIds={props.groupIds}
                    clickedPoint={clickedPoint}
                    onClose={() => setIsActivityFeedOpen(false)}
                />
            ) : null}
        </React.Fragment>
    );
};
